import { NotifyApi } from "./NotifyApi";
import { GoogleAnalytics } from "./GoogleAnalytics";
import { KlaviyoTracking } from "./KlaviyoTracking";
import { MetaPixel } from "./MetaPixel";
import type { WishlistSettings, WishlistUtils } from "../WishlistApp";
import type { ShopifyProduct, ShopifyProductVariant } from "./ShopifyApi";
import type { WishlistEvents, WishlistEvent } from "../utils/WishlistEvents";
import { createDebug } from "../utils/LazyDebug";
import type { ThemeHelper } from "src/theme-helper/ThemeHelper";
import type { Wishlist } from "./WishlistApi";

const debug = createDebug("wk:tracking");

export interface TrackingApiParams {
  settings: WishlistSettings;
  events: WishlistEvents;
  notifyHost: string;
  utils: WishlistUtils;
  theme: ThemeHelper;
  shop: string;
  sessionId?: string;
  customerId?: string;
  customerEmail?: string;
}

export class TrackingApi implements TrackingService {
  services: TrackingService[];
  utils: WishlistUtils;
  theme: ThemeHelper;

  constructor({
    settings,
    events,
    notifyHost,
    utils,
    theme,
    shop,
    sessionId,
    customerId,
    customerEmail,
  }: TrackingApiParams) {
    this.utils = utils;
    this.theme = theme;
    this.services = [];

    debug("Init tracking");

    if (settings.integrations.wishlistAnalytics.enabled) {
      this.services.push(
        new NotifyApi({
          host: notifyHost,
          shop,
          customerId,
          sessionId,
        }),
      );
    }

    if (settings.integrations.klaviyo.enabled && customerEmail) {
      debug("Add Klaviyo integration");
      this.services.push(
        new KlaviyoTracking({
          urlUtil: this.utils.url,
          moneyUtil: this.utils.money,
          customerEmail: customerEmail,
        }),
      );
    }

    if (settings.integrations.metaPixel.enabled) {
      debug("Add Meta Pixel integration");
      this.services.push(
        new MetaPixel({
          currency: window.Shopify.currency.active,
        }),
      );
    }

    if (settings.integrations.googleAnalytics.enabled) {
      debug("Add Google Analytics integration");
      this.services.push(
        new GoogleAnalytics({
          currency: window.Shopify.currency.active,
        }),
      );
    }

    const source = this.theme.getPageType();

    const trackStateEvent =
      (eventName: TrackingEventName) => (event: WishlistEvent) =>
        this.track({
          eventName,
          source,
          wishlist: event.data?.wishlist,
          product: event.data?.product,
          variant: event.data?.variant,
          quantity: event.data?.quantity,
        });

    events.subscribeMany({
      "wishlist:add-to-cart:success": trackStateEvent("addToCart"),
      "wishlist:add:success": trackStateEvent("addToWishlist"),
      "wishlist:change-variant:success": trackStateEvent("changeVariant"),
      "wishlist:clear:success": trackStateEvent("clearWishlist"),
      "wishlist:remove:success": trackStateEvent("removeFromWishlist"),
      "wishlist:share:success": trackStateEvent("shareWishlist"),
      "wishlist:page-view": trackStateEvent("viewWishlist"),
    });
  }

  async track(event: TrackingEvent) {
    debug("Track", event);

    if (!this.services.length) {
      return [];
    }

    return Promise.all(this.services.map((service) => service.track(event)));
  }

  setSessionId(sessionId: string) {
    const notifyApi = this.services.find(
      (service) => service instanceof NotifyApi,
    ) as NotifyApi | undefined;

    if (notifyApi) {
      notifyApi.setSession(sessionId);
    }
  }

  clearSessionId() {
    const notifyApi = this.services.find(
      (service) => service instanceof NotifyApi,
    ) as NotifyApi | undefined;

    if (notifyApi) {
      notifyApi.clearSession();
    }
  }
}

export interface TrackingService {
  track: (event: TrackingEvent) => Promise<any>;
}

export interface TrackingEvent {
  eventName: TrackingEventName;
  wishlist?: Wishlist;
  product?: ShopifyProduct | null;
  variant?: ShopifyProductVariant;
  quantity?: number;
  source?: string;
}

export type TrackingEventName =
  | "addToWishlist"
  | "removeFromWishlist"
  | "changeVariant"
  | "clearWishlist"
  | "shareWishlist"
  | "viewWishlist"
  | "addToCart";
